import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const giragliaSaintTropez: ChauffeurPage = {
  slug: "giraglia-saint-tropez",
  title: "Chauffeur privé Giraglia Saint-Tropez – VTC régate",
  subtitle:
    "Chauffeur privé pendant la Giraglia à Saint-Tropez : accès au port et au village de la régate, transferts aéroport et navettes dans le golfe.",
  keywords: [
    "chauffeur giraglia saint-tropez",
    "vtc giraglia",
    "chauffeur régate saint-tropez",
  ],
  hero: {
    title: "Chauffeur privé pendant la Giraglia à Saint-Tropez",
    subtitle:
      "Régate reliant Saint-Tropez à Gênes via l'île de Giraglia, en juin : un chauffeur dédié quand la presqu'île sature et que le port se ferme.",
    keywords: [
      "chauffeur Giraglia",
      "VTC Saint-Tropez régate",
      "navette golfe de Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant la Giraglia à Saint-Tropez",
    badges: [
      "Transferts aéroport",
      "Accès port & village",
      "Navettes du golfe",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant la Giraglia",
    items: [
      {
        title: "Presqu'île engorgée maîtrisée",
        description:
          "Une seule route mène à Saint-Tropez : en juin, elle sature. Le chauffeur anticipe les créneaux et vous rapproche du port sans vous garer.",
      },
      {
        title: "Transferts aéroport",
        description:
          "Depuis Nice Côte d'Azur, ou Toulon-Hyères plus proche : transfert avec suivi de vol vers Saint-Tropez.",
      },
      {
        title: "Port et village de la régate",
        description:
          "Dépose au plus près du port et des quais où équipages et armateurs se retrouvent pendant la Giraglia.",
      },
      {
        title: "Navettes du golfe",
        description:
          "Sainte-Maxime, Grimaud, Ramatuelle : liaisons dans le golfe de Saint-Tropez pour dîners et hébergements.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant la Giraglia",
    services: [
      {
        title: "Transfert aéroport ↔ Saint-Tropez",
        text: "Depuis Nice Côte d'Azur ou Toulon-Hyères, avec suivi de vol pendant la Giraglia.",
      },
      {
        title: "Accès port & village de régate",
        text: "Dépose au plus près des quais selon l'affluence et les fermetures autour du port.",
      },
      {
        title: "Navettes dans le golfe",
        text: "Liaisons Saint-Tropez, Sainte-Maxime, Grimaud et Ramatuelle pour équipages et invités.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour combiner régates côtières, village et rendez-vous.",
      },
      {
        title: "Soirées & dîners",
        text: "Restaurants du port et des plages de Pampelonne, retour de nuit assuré.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant la Giraglia",
    items: [
      {
        name: "Vieux port de Saint-Tropez",
        description:
          "Quais de la régate, où se retrouvent équipages et armateurs.",
      },
      {
        name: "Village & centre de Saint-Tropez",
        description: "Place des Lices, ruelles et adresses du centre.",
      },
      {
        name: "Plages de Pampelonne (Ramatuelle)",
        description: "Dîners et soirées en bord de mer.",
      },
      {
        name: "Sainte-Maxime & Grimaud",
        description: "Hébergements et liaisons de l'autre rive du golfe.",
      },
      {
        name: "Aéroports (Nice, Toulon-Hyères)",
        description: "Portes d'entrée vers la presqu'île de Saint-Tropez.",
      },
    ],
  },
  generic: {
    heading: "La Giraglia vue d'un chauffeur",
    subheading:
      "En juin, Saint-Tropez lance la régate de la Giraglia et la presqu'île se remplit d'équipages et d'invités.",
    paragraphs: [
      "Chaque mois de juin, la Giraglia relie Saint-Tropez à Gênes en contournant l'île de Giraglia, au large du cap Corse, après plusieurs jours de régates côtières dans le golfe. Le port et le village concentrent équipages, armateurs et partenaires : un chauffeur qui connaît la presqu'île vous rapproche des quais sans chercher à vous garer.",
      "Saint-Tropez n'est desservie que par une route unique, vite saturée en juin. Depuis l'aéroport de Nice Côte d'Azur, ou Toulon-Hyères plus proche, le chauffeur assure le transfert avec suivi de vol et absorbe les aléas de circulation.",
      "Entre régates côtières, village de la régate, dîners au port et soirées à Pampelonne, la mise à disposition garde le même chauffeur sur la journée et assure les liaisons dans le golfe et les retours de nuit.",
    ],
    bullets: [
      "Accès au plus près du port",
      "Transferts aéroport avec suivi de vol",
      "Navettes dans le golfe de Saint-Tropez",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour la Giraglia",
    steps: [
      "Indiquez votre aéroport d'arrivée, votre hébergement dans le golfe et vos horaires.",
      "Nous calons les transferts et navettes selon l'affluence de la presqu'île et du port.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées.",
      "Transfert direct ou mise à disposition pour régates côtières, village et soirées.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pour la Giraglia à Saint-Tropez ?",
      a: "Réservez à l'avance : en juin, Saint-Tropez et sa route unique saturent pendant la régate. Précisez votre aéroport, votre hébergement et vos horaires pour un chauffeur dédié sur la durée.",
    },
    {
      q: "Depuis quel aéroport assurez-vous le transfert ?",
      a: "Depuis Nice Côte d'Azur, la porte d'entrée principale, ou Toulon-Hyères, plus proche de la presqu'île. Dans les deux cas, avec suivi de vol et adaptation aux retards.",
    },
    {
      q: "Pouvez-vous déposer au plus près du port et du village de la régate ?",
      a: "Oui. Le chauffeur vous dépose au point autorisé le plus proche des quais selon l'affluence et les fermetures, puis vous reprend à la sortie.",
    },
    {
      q: "Proposez-vous des navettes dans le golfe de Saint-Tropez ?",
      a: "Oui. Nous assurons les liaisons entre Saint-Tropez, Sainte-Maxime, Grimaud et Ramatuelle pour équipages, invités et hébergements.",
    },
    {
      q: "Assurez-vous les retours après les dîners et soirées ?",
      a: "Oui. Dîners au port et soirées à Pampelonne se terminent tard : le chauffeur patiente et garantit le retour de nuit vers votre hébergement.",
    },
    {
      q: "Pourquoi choisir KS Rent Services pour la Giraglia ?",
      a: "Notre service de chauffeur privé sur la Côte d'Azur est noté 5,0/5 sur 38 avis Google : ponctualité et connaissance de Saint-Tropez et de son golfe.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Saint-Tropez, tarifs et trajets",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "Chauffeur Côte d’Azur, tarifs et trajets",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Accueil à l’aéroport de Nice", href: "/chauffeur/aeroport" },
    {
      label: "Louer une voiture à Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur pour la Giraglia",
    text: "Transferts aéroport, accès au port et navettes du golfe : réservez un chauffeur dédié pour la régate.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur Giraglia Saint-Tropez — VTC régate",
    description:
      "Chauffeur privé pendant la Giraglia à Saint-Tropez : transferts aéroport avec suivi de vol, accès au port et au village, navettes dans le golfe. Réservez.",
    canonical: "/chauffeur/giraglia-saint-tropez",
  },
};

export default giragliaSaintTropez;
